import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createStripePromotionCode,
  deactivateStripePromotionCode,
  fetchStripePromotionCodes,
  fetchBillingPlans,
} from '../../api/adminClient';
import { formatDateTime } from '../../utils/formatters';

const emptyCreateResult = () => ({
  success: null,
  message: '',
  payload: null,
});

const formatPromoExpires = (value) => {
  if (value == null) return '—';
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value > 1e12 ? value : value * 1000;
    return formatDateTime(new Date(ms).toISOString());
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) {
      const n = Number(trimmed);
      const ms = n > 1e12 ? n : n * 1000;
      return formatDateTime(new Date(ms).toISOString());
    }
  }
  return formatDateTime(value);
};

const formatAmountOff = (amountOff, currency) => {
  if (amountOff == null) return null;
  const numericAmount = Number(amountOff);
  if (!Number.isFinite(numericAmount)) return null;

  const normalizedCurrency = (currency || 'usd').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: normalizedCurrency,
      maximumFractionDigits: 2,
    }).format(numericAmount / 100);
  } catch {
    const fallbackAmount = (numericAmount / 100).toFixed(2);
    return normalizedCurrency === 'USD'
      ? `$${fallbackAmount}`
      : `${fallbackAmount} ${normalizedCurrency}`;
  }
};

const describeCouponEffect = (item) => {
  const coupon = item?.coupon;
  if (!coupon || typeof coupon !== 'object') return 'Discount details unavailable.';

  let discountText = 'a discount';
  if (coupon.percent_off != null) {
    discountText = `${coupon.percent_off}% off`;
  } else if (coupon.amount_off != null) {
    const formattedAmount = formatAmountOff(coupon.amount_off, coupon.currency);
    discountText = formattedAmount ? `${formattedAmount} off` : 'a fixed discount';
  } else if (coupon.name) {
    discountText = coupon.name;
  }

  let durationText = '';
  if (coupon.duration === 'once') {
    durationText = 'on the first payment';
  } else if (coupon.duration === 'forever') {
    durationText = 'on every payment going forward';
  } else if (coupon.duration === 'repeating') {
    const months = Number(coupon.duration_in_months);
    durationText =
      Number.isFinite(months) && months > 0
        ? `on each payment for ${months} ${months === 1 ? 'month' : 'months'}`
        : 'for a limited time';
  }

  const parts = [`Gives ${discountText}${durationText ? ` ${durationText}` : ''}.`];

  if (item?.restrictions?.first_time_transaction) {
    parts.push('Only for first-time transactions.');
  }

  if (item?.expires_at != null) {
    parts.push(`Code expires ${formatPromoExpires(item.expires_at)}.`);
  }

  return parts.join(' ');
};

const formatCreateSuccess = (data) => {
  const pc = data?.promotionCode;
  if (!pc || typeof pc !== 'object') return null;
  return (
    <ul className="small mb-0 ps-3">
      <li>
        <strong>Customer code:</strong> {pc.code ?? '—'}
      </li>
      <li>
        <strong>Active:</strong> {pc.active ? 'Yes' : 'No'}
      </li>
      {pc.expires_at != null && (
        <li>
          <strong>Expires:</strong> {formatPromoExpires(pc.expires_at)}
        </li>
      )}
      {pc.max_redemptions != null && (
        <li>
          <strong>Max uses:</strong> {pc.max_redemptions}
        </li>
      )}
    </ul>
  );
};

const CouponCodeManagement = () => {
  const [couponSource, setCouponSource] = useState('reuse');
  const [couponId, setCouponId] = useState('');
  const [percentOff, setPercentOff] = useState('');
  const [amountDollars, setAmountDollars] = useState('');
  const [currency, setCurrency] = useState('usd');
  const [duration, setDuration] = useState('once');
  const [durationInMonths, setDurationInMonths] = useState('');
  const [couponName, setCouponName] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const [planUuid, setPlanUuid] = useState('');
  const [plans, setPlans] = useState([]);

  const [deactivateCode, setDeactivateCode] = useState('');

  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [deactivateSubmitting, setDeactivateSubmitting] = useState(false);
  const [createResult, setCreateResult] = useState(() => emptyCreateResult());
  const [deactivateResult, setDeactivateResult] = useState(() => emptyCreateResult());

  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [listPayload, setListPayload] = useState(null);
  const [listCursor, setListCursor] = useState(null);
  const [listRefresh, setListRefresh] = useState(0);
  const [draftListFilters, setDraftListFilters] = useState({
    limit: 50,
    active: '',
    coupon: '',
    code: '',
  });
  const [appliedListFilters, setAppliedListFilters] = useState({
    limit: 50,
    active: '',
    coupon: '',
    code: '',
  });

  useEffect(() => {
    fetchBillingPlans()
      .then((res) => {
        if (res.data?.success) setPlans(res.data.data ?? []);
      })
      .catch(() => {});
  }, []);

  const bumpListRefresh = useCallback(() => {
    setListRefresh((n) => n + 1);
  }, []);

  const buildListParams = useCallback(
    (startingAfter) => {
      const lim = Math.min(
        100,
        Math.max(1, parseInt(String(appliedListFilters.limit), 10) || 50)
      );
      const params = { limit: lim };
      if (startingAfter) params.starting_after = startingAfter;
      if (appliedListFilters.active === 'true') params.active = true;
      if (appliedListFilters.active === 'false') params.active = false;
      const cp = appliedListFilters.coupon.trim();
      if (cp) params.coupon = cp;
      const cod = appliedListFilters.code.trim();
      if (cod) params.code = cod;
      return params;
    },
    [appliedListFilters]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setListLoading(true);
      setListError(null);
      try {
        const res = await fetchStripePromotionCodes(buildListParams(listCursor));
        const data = res.data;
        if (!data?.success) {
          throw new Error(data?.error || 'Could not load codes.');
        }
        if (!cancelled) {
          setListPayload(data);
        }
      } catch (e) {
        if (!cancelled) {
          setListPayload(null);
          setListError(
            e.response?.data?.error || e.message || 'Could not load codes.'
          );
        }
      } finally {
        if (!cancelled) {
          setListLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [buildListParams, listCursor, listRefresh]);

  const applyListFilters = (e) => {
    e?.preventDefault?.();
    setAppliedListFilters({ ...draftListFilters });
    setListCursor(null);
  };

  const listItems = listPayload?.data ?? [];
  const listHasMore = Boolean(listPayload?.has_more);
  const goListNext = () => {
    if (!listItems.length) return;
    setListCursor(listItems[listItems.length - 1]?.id ?? null);
  };
  const goListFirst = () => setListCursor(null);

  const hasPercent = useMemo(() => percentOff.trim() !== '', [percentOff]);
  const hasDollars = useMemo(() => amountDollars.trim() !== '', [amountDollars]);

  const buildCreatePayload = () => {
    const body = {};

    if (couponSource === 'reuse') {
      const id = couponId.trim();
      if (!id) {
        return { error: 'Paste the discount ID you were given (from billing or support).' };
      }
      body.couponId = id;
    } else {
      if (hasPercent && hasDollars) {
        return {
          error:
            'Choose either “percent off the bill” or “fixed dollars off”—not both.',
        };
      }
      if (!hasPercent && !hasDollars) {
        return {
          error:
            'Set how much to take off: either a percent or a dollar amount.',
        };
      }
      if (hasPercent) {
        const p = Number(percentOff);
        if (!Number.isFinite(p) || p < 1 || p > 100) {
          return { error: 'Percent off should be a whole number from 1 to 100.' };
        }
        body.percent_off = p;
      } else {
        const dollars = Number(amountDollars);
        if (!Number.isFinite(dollars) || dollars <= 0) {
          return { error: 'Enter a dollar amount greater than zero.' };
        }
        const cents = Math.round(dollars * 100);
        if (cents < 1) {
          return { error: 'The amount is too small after rounding. Enter a larger value.' };
        }
        body.amount_off = cents;
        body.currency = (currency || 'usd').trim().toLowerCase() || 'usd';
      }

      const d = duration || 'once';
      if (!['once', 'repeating', 'forever'].includes(d)) {
        return { error: 'Please pick a valid duration from the list.' };
      }
      body.duration = d;
      if (d === 'repeating') {
        const m = parseInt(durationInMonths, 10);
        if (!Number.isFinite(m) || m < 1) {
          return {
            error:
              'For “same discount each month for a while,” enter how many months it should last.',
          };
        }
        body.duration_in_months = m;
      }
      const n = couponName.trim();
      if (n) body.name = n;
    }

    const c = promoCode.trim();
    if (!c) {
      return {
        error:
          'Enter the code customers will type at checkout (for example VIP40). If it’s left blank, Stripe may create a random code that’s hard to share.',
      };
    }
    body.code = c;
    const mr = maxRedemptions.trim();
    if (mr !== '') {
      const n = parseInt(mr, 10);
      if (!Number.isFinite(n) || n < 1) {
        return { error: '“How many times can this be used?” should be a whole number, 1 or more.' };
      }
      body.max_redemptions = n;
    }

    if (expiresAt.trim()) {
      const dt = new Date(expiresAt);
      if (Number.isNaN(dt.getTime())) {
        return { error: 'That stop date doesn’t look valid. Check the date and time.' };
      }
      body.expires_at = dt.toISOString();
    }

    if (planUuid) body.planUuid = planUuid;

    return { body };
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateResult(emptyCreateResult());
    const built = buildCreatePayload();
    if (built.error) {
      setCreateResult({ success: false, message: built.error, payload: null });
      return;
    }
    setCreateSubmitting(true);
    try {
      const res = await createStripePromotionCode(built.body);
      const data = res.data;
      if (data?.success) {
        setCreateResult({
          success: true,
          message:
            'The coupon code is set up. Share the customer code with anyone who should use it at checkout.',
          payload: data,
        });
        setListCursor(null);
        bumpListRefresh();
      } else {
        setCreateResult({
          success: false,
          message: data?.error || 'Something went wrong. Try again or contact support.',
          payload: data,
        });
      }
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.message ||
        'Something went wrong. Try again or contact support.';
      setCreateResult({ success: false, message: msg, payload: err.response?.data });
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleDeactivate = async (e) => {
    e.preventDefault();
    setDeactivateResult(emptyCreateResult());
    const code = deactivateCode.trim();
    if (!code) {
      setDeactivateResult({
        success: false,
        message: 'Enter the code customers type at checkout.',
        payload: null,
      });
      return;
    }
    const body = { code };
    setDeactivateSubmitting(true);
    try {
      const res = await deactivateStripePromotionCode(body);
      const data = res.data;
      if (data?.success) {
        setDeactivateResult({
          success: true,
          message:
            'That code is turned off. New checkouts won’t be able to use it.',
          payload: data,
        });
        setListCursor(null);
        bumpListRefresh();
      } else {
        setDeactivateResult({
          success: false,
          message: data?.error || 'Could not turn off the code. Try again or contact support.',
          payload: data,
        });
      }
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.message ||
        'Could not turn off the code. Try again or contact support.';
      setDeactivateResult({
        success: false,
        message: msg,
        payload: err.response?.data,
      });
    } finally {
      setDeactivateSubmitting(false);
    }
  };

  return (
    <div className="coupon-codes-view">
      <div className="mb-4">
        <h1 className="display-6 fw-semibold mb-2">Coupon codes</h1>
        <p className="text-muted mb-0">
          Give customers a code they can enter at checkout to get a discount. Here you can
          create a new code or stop an old one from working.
        </p>
      </div>

      <div className="card-soft mb-4">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-start gap-3 mb-3">
          <div>
            <h2 className="h5 fw-semibold mb-1">Existing promotion codes</h2>
            <p className="text-muted small mb-0">
              Codes customers can type at checkout, straight from billing. Filter or go to the next
              page if there are many.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm rounded-pill"
            disabled={listLoading}
            onClick={() => {
              setListCursor(null);
              bumpListRefresh();
            }}
          >
            Refresh
          </button>
        </div>

        <form
          className="row g-2 align-items-end mb-4"
          onSubmit={applyListFilters}
        >
          <div className="col-6 col-md-2">
            <label className="form-label small mb-1">Rows per page</label>
            <select
              className="form-select form-select-sm"
              value={draftListFilters.limit}
              onChange={(e) =>
                setDraftListFilters((f) => ({ ...f, limit: e.target.value }))
              }
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label small mb-1">Active</label>
            <select
              className="form-select form-select-sm"
              value={draftListFilters.active}
              onChange={(e) =>
                setDraftListFilters((f) => ({ ...f, active: e.target.value }))
              }
            >
              <option value="">All</option>
              <option value="true">On</option>
              <option value="false">Off</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label small mb-1">Discount (coupon) ID</label>
            <input
              type="text"
              className="form-control form-control-sm font-monospace"
              placeholder="jYy…"
              value={draftListFilters.coupon}
              onChange={(e) =>
                setDraftListFilters((f) => ({ ...f, coupon: e.target.value }))
              }
              autoComplete="off"
            />
          </div>
          <div className="col-md-3">
            <label className="form-label small mb-1">Customer code (exact)</label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="VIP40"
              value={draftListFilters.code}
              onChange={(e) =>
                setDraftListFilters((f) => ({ ...f, code: e.target.value }))
              }
              autoComplete="off"
            />
          </div>
          <div className="col-md-2">
            <button type="submit" className="btn btn-primary btn-sm w-100 rounded-pill">
              Apply filters
            </button>
          </div>
        </form>

        {listError && (
          <div className="alert alert-danger mb-0" role="alert">
            {listError}
          </div>
        )}

        {!listError && listLoading && (
          <p className="text-muted small mb-0">Loading…</p>
        )}

        {!listError && !listLoading && listItems.length === 0 && (
          <p className="text-muted small mb-0">No codes match these filters.</p>
        )}

        {!listError && !listLoading && listItems.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-2">
                <thead>
                  <tr>
                    <th scope="col">Customer code</th>
                    <th scope="col">On?</th>
                    <th scope="col">Uses</th>
                    <th scope="col">Max uses</th>
                    <th scope="col">Expires</th>
                    <th scope="col">What this coupon does</th>
                  </tr>
                </thead>
                <tbody>
                  {listItems.map((row) => (
                    <tr key={row.id}>
                      <td className="fw-medium">{row.code ?? '—'}</td>
                      <td>
                        {row.active ? (
                          <span className="badge text-bg-success">On</span>
                        ) : (
                          <span className="badge text-bg-secondary">Off</span>
                        )}
                      </td>
                      <td>{row.times_redeemed ?? '—'}</td>
                      <td>{row.max_redemptions ?? '—'}</td>
                      <td className="text-muted small">{formatPromoExpires(row.expires_at)}</td>
                      <td className="small">{describeCouponEffect(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex flex-wrap align-items-center gap-2 justify-content-between">
              <span className="text-muted small">
                Showing {listItems.length} on this page
                {listHasMore ? ' · more available' : ''}.
              </span>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm rounded-pill"
                  disabled={!listCursor}
                  onClick={goListFirst}
                >
                  First page
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm rounded-pill"
                  disabled={!listHasMore}
                  onClick={goListNext}
                >
                  Next page
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="row g-4">
        <div className="col-12 col-xl-7">
          <div className="card-soft h-100">
            <h2 className="h5 fw-semibold mb-1">Create a coupon code</h2>
            <p className="text-muted small mb-4">
              First choose whether you’re reusing an existing discount we already set up, or
              you’re defining a brand-new discount and code together.
            </p>
            <form onSubmit={handleCreate}>
              <div className="mb-4">
                <label className="form-label fw-medium">What are you starting from?</label>
                <select
                  className="form-select"
                  value={couponSource}
                  onChange={(e) => setCouponSource(e.target.value)}
                >
                  <option value="reuse">
                    I already have a discount ID (from Stripe or our team)
                  </option>
                  <option value="new">I’m creating a new discount from scratch</option>
                </select>
              </div>

              {couponSource === 'reuse' ? (
                <div className="mb-4">
                  <label className="form-label fw-medium">Discount ID</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Paste the ID here"
                    value={couponId}
                    onChange={(e) => setCouponId(e.target.value)}
                    autoComplete="off"
                  />
                  <div className="form-text">
                    This is the long ID tied to the discount rules—not the short word customers
                    type. If you only have a customer-facing word, pick “creating a new discount”
                    above or use “Turn off a code” with that word.
                  </div>
                </div>
              ) : (
                <div className="mb-4 p-3 rounded-3 bg-light border border-light">
                  <p className="small fw-medium text-secondary mb-3">New discount details</p>
                  <div className="row g-3 mb-2">
                    <div className="col-md-6">
                      <label className="form-label">Percent off the bill</label>
                      <div className="input-group">
                        <input
                          type="number"
                          min={1}
                          max={100}
                          className="form-control"
                          value={percentOff}
                          onChange={(e) => setPercentOff(e.target.value)}
                          disabled={hasDollars}
                          placeholder="e.g. 15"
                        />
                        <span className="input-group-text">%</span>
                      </div>
                      <div className="form-text">Leave blank if you’re using dollars off instead.</div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Fixed dollars off</label>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          type="number"
                          min={0.01}
                          step={0.01}
                          className="form-control"
                          value={amountDollars}
                          onChange={(e) => setAmountDollars(e.target.value)}
                          disabled={hasPercent}
                          placeholder="e.g. 10.00"
                        />
                      </div>
                      <div className="form-text">Leave blank if you’re using a percent instead.</div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Currency</label>
                    <input
                      type="text"
                      className="form-control"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      disabled={!hasDollars}
                    />
                    <div className="form-text">
                      Only matters for “dollars off.” Most of the time this stays{' '}
                      <strong>usd</strong>.
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">How long should this discount apply?</label>
                    <select
                      className="form-select"
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                    >
                      <option value="once">Only the first charge (one time)</option>
                      <option value="repeating">Every month for a set number of months</option>
                      <option value="forever">On every bill going forward</option>
                    </select>
                  </div>
                  {duration === 'repeating' && (
                    <div className="mb-3">
                      <label className="form-label">Number of months</label>
                      <input
                        type="number"
                        min={1}
                        className="form-control"
                        value={durationInMonths}
                        onChange={(e) => setDurationInMonths(e.target.value)}
                        placeholder="e.g. 6"
                      />
                      <div className="form-text">
                        The same discount repeats each month for this many months.
                      </div>
                    </div>
                  )}
                  <div className="mb-0">
                    <label className="form-label">Name (optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={couponName}
                      onChange={(e) => setCouponName(e.target.value)}
                      placeholder="e.g. Spring referral discount"
                    />
                    <div className="form-text">
                      For your own records in billing—customers don’t see this.
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label className="form-label fw-medium">Apply to plan (optional)</label>
                <select
                  className="form-select"
                  value={planUuid}
                  onChange={(e) => setPlanUuid(e.target.value)}
                >
                  <option value="">Across Platform</option>
                  {plans.map((p) => (
                    <option key={p.uuid} value={p.uuid}>
                      {p.plan_name} (${p.plan_price})
                    </option>
                  ))}
                </select>
                <div className="form-text">
                  Leave as "Across Platform" to apply the discount to any plan.
                </div>
              </div>

              <hr className="my-4" />
              <h3 className="h6 fw-semibold mb-1">The code at checkout</h3>
              <p className="text-muted small mb-3">
                These settings control the actual word or phrase customers enter when they pay.
              </p>

              <div className="mb-3">
                <label className="form-label fw-medium">
                  Code customers will type <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  autoComplete="off"
                  required
                  placeholder="e.g. VIP40"
                />
                <div className="form-text">
                  Required. This must match exactly what you tell customers (Stripe may generate a
                  meaningless code if this is empty).
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">How many times can this be used? (optional)</label>
                <input
                  type="number"
                  min={1}
                  className="form-control"
                  value={maxRedemptions}
                  onChange={(e) => setMaxRedemptions(e.target.value)}
                  placeholder="Leave blank for no limit"
                />
                <div className="form-text">Stops working after this many successful uses.</div>
              </div>
              <div className="mb-4">
                <label className="form-label">Stop working after this date &amp; time (optional)</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
                <div className="form-text">In your computer’s local time zone.</div>
              </div>

              <button
                type="submit"
                className="btn btn-primary rounded-pill shadow-sm"
                disabled={createSubmitting}
              >
                {createSubmitting ? 'Creating…' : 'Create coupon code'}
              </button>
            </form>

            {createResult.message && (
              <div
                className={`alert mt-4 mb-0 ${
                  createResult.success ? 'alert-success' : 'alert-danger'
                }`}
                role="alert"
              >
                <div className="fw-semibold mb-2">{createResult.message}</div>
                {createResult.success && formatCreateSuccess(createResult.payload)}
              </div>
            )}
          </div>
        </div>

        <div className="col-12 col-xl-5">
          <div className="card-soft h-100">
            <h2 className="h5 fw-semibold mb-1">Turn off a coupon code</h2>
            <p className="text-muted small mb-4">
              Use this when a promotion should end. Customers won’t be able to use the code on
              new purchases after you turn it off.
            </p>
            <form onSubmit={handleDeactivate}>
              <div className="mb-4">
                <label className="form-label">Customer-facing code</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. SAVE10"
                  value={deactivateCode}
                  onChange={(e) => setDeactivateCode(e.target.value)}
                  autoComplete="off"
                />
                <div className="form-text">
                  If you used this code more than once in the system, we turn off the first active
                  match.
                </div>
              </div>
              <button
                type="submit"
                className="btn btn-outline-danger rounded-pill shadow-sm"
                disabled={deactivateSubmitting}
              >
                {deactivateSubmitting ? 'Turning off…' : 'Turn off this code'}
              </button>
            </form>

            {deactivateResult.message && (
              <div
                className={`alert mt-4 mb-0 ${
                  deactivateResult.success ? 'alert-success' : 'alert-danger'
                }`}
                role="alert"
              >
                <div className="fw-semibold mb-2">{deactivateResult.message}</div>
                {deactivateResult.success && deactivateResult.payload?.promotionCode && (
                  <ul className="small mb-0 ps-3">
                    <li>
                      <strong>Code:</strong> {deactivateResult.payload.promotionCode.code ?? '—'}
                    </li>
                    <li>
                      <strong>Still active:</strong>{' '}
                      {deactivateResult.payload.promotionCode.active ? 'Yes' : 'No'}
                    </li>
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CouponCodeManagement;
